import type { ReactNode } from 'react';
import type { Token } from '@vif/core';
import { READER_FONT_STACKS } from '../app/fonts.js';
import { layoutChunk, type ReaderFont } from '../reader/orp-layout.js';

/**
 * The RSVP stage: renders the current chunk with the ORP glyph center
 * pinned to anchorX (38% of the reading stage, PRD §8.1). Words are
 * positioned by summed font-metric advances — no DOM measurement — with
 * kerning and ligatures disabled so browser advances match the metrics.
 *
 * Chunk mode (PRD §7.5, P1-F4): vertical stacks members left-aligned on
 * the LONGEST word, whose ORP defines the anchor and alone carries the
 * highlight; horizontal lays them in a row with the ORP on the FIRST.
 * The geometry lives in layoutChunk (orp-layout.ts), unit-tested there.
 */
export function WordDisplay({
  tokens,
  font,
  fontSizePx,
  anchorX,
  layout,
  showGuides,
  orpColor,
}: {
  tokens: readonly Token[];
  font: ReaderFont;
  fontSizePx: number;
  anchorX: number;
  layout: 'vertical' | 'horizontal';
  showGuides: boolean;
  orpColor: string | null;
}): ReactNode {
  if (tokens.length === 0) return null;

  const style = {
    fontFamily: READER_FONT_STACKS[font],
    fontSize: `${fontSizePx}px`,
    ...(orpColor === null ? {} : ({ '--orp-color': orpColor } as Record<string, string>)),
  };

  const lines = layoutChunk(tokens, layout, font, fontSizePx, anchorX);

  return (
    <div className="word-track" style={style}>
      {showGuides && (
        <>
          <div
            className="guide-notch top"
            style={{
              left: anchorX,
              top: `calc(50% - ${(lines.length * 1.35 * fontSizePx) / 2 + 14}px)`,
              position: 'absolute',
            }}
          />
          <div
            className="guide-notch bottom"
            style={{
              left: anchorX,
              top: `calc(50% + ${(lines.length * 1.35 * fontSizePx) / 2 + 8}px)`,
              position: 'absolute',
            }}
          />
        </>
      )}
      {lines.map((line, lineIdx) => (
        <div className="word-line" key={lineIdx}>
          {line.map((member, i) => (
            <span className="word-box" style={{ left: member.left }} key={i}>
              <span>{member.word.before}</span>
              {member.highlighted ? (
                <span className="orp">{member.word.orp}</span>
              ) : (
                <span>{member.word.orp}</span>
              )}
              <span>{member.word.after}</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
